import * as R from 'ramda'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import Avatar from '../../components/Avatar'
import SvgIcon from '../../components/SvgIcon'
import { DEFAULT_PROFILE_URL } from '../../constants'
import { useTranslation } from '../../i18n'
import ArrowDownSvg from '../../public/static/images/icon-arrow-down.svg'
import { getWidgetDonorsRequest } from '../../redux/donor/actions'
import { clearRedux } from '../../redux/master/actions'
import { getColor, getFontSize } from '../../utils/cssUtils'
import { isExist } from '../../utils/ramdaUtils'

const Wrapper = styled.div`
  width: 100%;
  padding: 24px 0;
`
const Title = styled.h3`
  font-size: ${getFontSize('title')};
  font-weight: 600;
  padding-bottom: 8px;
  border-bottom: solid 1px ${getColor('border')};
`

const DonorItem = styled.div`
  display: flex;
  align-items: center;
  padding: 23px 0;
  border-bottom: solid 1px ${getColor('border')};
`

const StyledAvatar = styled(Avatar)`
  width: 48px;
  height: 48px;
`

const DonationInfo = styled.div`
  margin-left: 8px;
  font-size: ${getFontSize('body2')};
`
const Donor = styled.div`
  color: ${getColor('black')};
`
const DonationAmount = styled.div`
  color: ${getColor('grey')};
`

const DonorMore = styled.a`
  display: flex;
  justify-content: center;
  border-radius: 4px;
  background-color: ${getColor('input', 0.04)};
  height: 48px;
  line-height: 48px;
  color: ${getColor('grey')};
  font-size: ${getFontSize('body2')};
  cursor: pointer;
`
const MoreText = styled.span`
  text-decoration: underline;
  margin-left: 4px;
`
const IconMore = styled(ArrowDownSvg)`
  width: 19px;
  height: 19px;
`

const DonorList = React.memo(({ projectNo }) => {
  const { t } = useTranslation('common')

  const dispatch = useDispatch()

  const [page, limit, hasMoreDonors, donors] = useSelector(
    R.paths([
      ['donorState', 'page'],
      ['donorState', 'limit'],
      ['donorState', 'hasMoreDonors'],
      ['donorState', 'donors']
    ]),
    R.equals
  )

  useEffect(() => {
    dispatch(getWidgetDonorsRequest(projectNo, page, limit, 'createdTime'))

    return () => {
      dispatch(clearRedux(['donorState', 'donors']))
      dispatch(clearRedux(['donorState', 'hasMoreDonors']))
      dispatch(clearRedux(['donorState', 'page']))
    }
  }, [])

  const donorRequest = useCallback(() => {
    // TODO: add loading for fetching new donors
    dispatch(
      getWidgetDonorsRequest(projectNo, R.inc(page), limit, 'createdTime')
    )
  }, [page])

  const getAvatarUrl = useMemo(
    () =>
      R.pipe(
        R.pathOr('', ['donor', 'avatarUrl']),
        R.when(R.isEmpty, R.always(DEFAULT_PROFILE_URL))
      ),
    []
  )

  const getDisplayName = useMemo(
    () =>
      R.pipe(
        R.pathOr('', ['donor', 'displayName']),
        R.when(R.isEmpty, R.always(t('personalFundraising.anonymousDonor')))
      ),
    []
  )

  return isExist(donors) ? (
    <Wrapper>
      <Title>{t('personalFundraising.donor')}</Title>
      {R.addIndex(R.map)(
        (donor, index) => (
          <DonorItem key={index}>
            <StyledAvatar src={getAvatarUrl(donor)} />
            <DonationInfo>
              <Donor>{getDisplayName(donor)}</Donor>
              <DonationAmount>
                {t('common.currency')}
                {donor.amount / 100}
              </DonationAmount>
            </DonationInfo>
          </DonorItem>
        ),
        donors
      )}
      {hasMoreDonors && (
        <DonorMore onClick={donorRequest}>
          <SvgIcon svg={<IconMore />} />
          <MoreText>{t('personalFundraising.ctaButton.more')}</MoreText>
        </DonorMore>
      )}
    </Wrapper>
  ) : null
})

export default DonorList
